import { memo, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useIntl } from "react-intl";
import Markdown, { type Components, defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

// The agent links each person it names as `[Full Name](person:<uuid>)` (ADR-0014).
// We render the name as plain text followed by an info icon that triggers the
// deterministic profile load — never as a navigable anchor.
const PERSON_HREF = /^person:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;

// Renders the Assistant's markdown replies. Tailwind's preflight resets list
// markers and heading sizes, so every element is mapped back to the design
// language explicitly (rather than pulling in @tailwindcss/typography). Raw
// HTML is not enabled — react-markdown escapes it by default.
const baseComponents: Components = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-0.5 pl-5 last:mb-0 marker:text-ink/40">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-0.5 pl-5 last:mb-0 marker:text-ink/40">{children}</ol>
  ),
  li: ({ children }) => <li className="pl-0.5">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  code: ({ children }) => (
    <code className="rounded bg-ink/10 px-1 py-0.5 text-[0.85em]">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded bg-ink/5 p-2 text-xs last:mb-0 [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </pre>
  ),
  h1: ({ children }) => <h3 className="mb-1 mt-2 font-semibold first:mt-0">{children}</h3>,
  h2: ({ children }) => <h3 className="mb-1 mt-2 font-semibold first:mt-0">{children}</h3>,
  h3: ({ children }) => <h3 className="mb-1 mt-2 font-semibold first:mt-0">{children}</h3>,
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-ink/15 pl-2 text-ink/70 last:mb-0">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-2 border-ink/10" />,
  // GFM tables (the multi-person comparison): preflight strips table chrome, so map it back.
  // Wrapped so a wide table scrolls horizontally inside the narrow drawer.
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="w-full border-collapse text-left text-[13px]">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-ink/15 px-2 py-1 font-semibold whitespace-nowrap">{children}</th>
  ),
  td: ({ children }) => <td className="border-t border-ink/10 px-2 py-1 align-top">{children}</td>,
};

// react-markdown's default url sanitizer drops unknown protocols, which would
// strip our `person:` scheme; let it through, sanitize everything else normally.
const urlTransform = (url: string) => (url.startsWith("person:") ? url : defaultUrlTransform(url));

// Click-to-zoom lightbox for reference-corpus figures (ADR 0017). Opens fit-to-window so the whole
// figure is visible; clicking the image toggles to native 100% inside a scrollable container, so the
// fine detail (high-DPI maps) is readable. Native <dialog> gives Esc + focus-trap + backdrop for free.
// Remounted per open (conditional render), so `zoomed` resets each time.
function Lightbox({
  src,
  alt,
  closeLabel,
  onClose,
}: {
  src: string;
  alt: string;
  closeLabel: string;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el && !el.open) el.showModal();
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => onClose(); // fires on Esc and programmatic close
    el.addEventListener("close", handler);
    // The native modal already closes itself on Esc; stop that keydown from bubbling to window so a
    // global Esc handler (the AssistantDrawer closes the chat on Escape) doesn't ALSO fire on the
    // same keypress. Propagation only — never preventDefault, or the dialog's own Esc-close is lost.
    const stopEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") e.stopPropagation();
    };
    el.addEventListener("keydown", stopEsc);
    return () => {
      el.removeEventListener("close", handler);
      el.removeEventListener("keydown", stopEsc);
    };
  }, [onClose]);

  // Portaled to <body>: the drawer uses `transform` (translate-x for the slide-in), which would
  // otherwise become the containing block for this fixed/top-layer dialog and collapse it to the
  // drawer's width. As a direct child of <body> it sizes to the viewport.
  return createPortal(
    <dialog
      ref={ref}
      onClick={onClose} // click anywhere (dimmed area) closes; the image stops propagation
      className="fixed inset-0 z-[1000] m-0 h-[100dvh] w-[100vw] max-h-none max-w-none border-0 bg-transparent p-0 backdrop:bg-ink/80"
    >
      {/* `flex` + `m-auto` on the image: centers it when it fits, and lets you pan to every edge
          when it overflows (plain `justify-center` would clip the top-left and make it unscrollable). */}
      <div className={zoomed ? "flex h-full w-full overflow-auto" : "flex h-full w-full items-center justify-center p-4"}>
        <img
          src={src}
          alt={alt}
          onClick={(e) => {
            e.stopPropagation();
            setZoomed((z) => !z);
          }}
          className={
            zoomed
              ? "m-auto h-auto max-w-none cursor-zoom-out"
              : "max-h-[90dvh] max-w-[90vw] object-contain cursor-zoom-in"
          }
        />
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label={closeLabel}
        className="fixed right-3 top-3 z-[1001] grid h-9 w-9 place-items-center rounded-full bg-ink/60 text-lg text-surface transition hover:bg-ink/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface/50"
      >
        ✕
      </button>
    </dialog>,
    document.body,
  );
}

// A trailing UNTERMINATED markdown link — `[text](…` with no closing `)` yet. During streaming the
// agent emits `[Name](person:<uuid>)` token by token; until the `)` lands, react-markdown renders
// the half-typed link as raw text (`[Ivan Izzo](person:01a6…`). We collapse that tail to just its
// visible text so the name shows cleanly and upgrades to the icon once the link closes. `$`-anchored,
// so a completed link earlier in the text is untouched.
const TRAILING_OPEN_LINK = /\[([^\]]*)\]\([^)]*$/;

export const AgentMarkdown = memo(function AgentMarkdown({
  content,
  onPersonInfo,
  addedIds,
  streaming = false,
}: {
  content: string;
  onPersonInfo?: (personId: string) => void;
  // Person ids currently accumulating into a comparison batch — their icons show an "added" state.
  addedIds?: ReadonlySet<string>;
  streaming?: boolean;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const infoLabel = intl.formatMessage({
    id: "agent.personInfo.label",
    defaultMessage: "Load full profile",
  });
  const expandLabel = intl.formatMessage({ id: "agent.expandImage", defaultMessage: "View full size" });
  const closeLabel = intl.formatMessage({ id: "agent.closeImage", defaultMessage: "Close image" });

  // The figure currently popped out in the lightbox (null = closed). AgentMarkdown is the only image
  // renderer, so the state lives here — no prop-drilling through AssistantDrawer.
  const [zoom, setZoom] = useState<{ src: string; alt: string } | null>(null);

  const components = useMemo<Components>(
    () => ({
      ...baseComponents,
      // Reference-corpus figures: constrain to the drawer width and open the lightbox on activate.
      // A real <button> wrapper (not a click-only <img>) makes the trigger keyboard- and
      // screen-reader-reachable — Tab to focus, Enter/Space to open, visible :focus-visible ring.
      img: ({ src, alt }) => {
        const url = typeof src === "string" ? src : undefined;
        // Only corpus figures (served same-origin via the /ai/images proxy) are real, zoomable images.
        // Any other src is blocked by CSP `img-src 'self'`, so rendering it as an <img> yields a
        // broken-image icon that's still clickable and opens an empty lightbox. Render those as inert
        // alt-text instead — no broken image, no fake zoom trigger, no cross-origin load attempt.
        if (!url || !url.startsWith("/ai/images/")) {
          return alt ? <span className="text-ink/50">{alt}</span> : null;
        }
        return (
          <button
            type="button"
            onClick={() => setZoom({ src: url, alt: alt ?? "" })}
            aria-label={expandLabel}
            title={expandLabel}
            className="my-2 inline-block max-w-full cursor-zoom-in rounded-md p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
          >
            <img src={url} alt={alt ?? ""} loading="lazy" className="block h-auto max-w-full rounded-md" />
          </button>
        );
      },
      a: ({ href, children }) => {
        const person = href ? PERSON_HREF.exec(href) : null;
        const personId = person?.[1];
        if (personId && onPersonInfo) {
          const id = personId;
          const added = addedIds?.has(id) ?? false;
          return (
            <span className="whitespace-nowrap">
              {children}
              <button
                type="button"
                onClick={() => onPersonInfo(id)}
                aria-label={infoLabel}
                title={infoLabel}
                className={`ml-0.5 inline-grid h-4 w-4 translate-y-0.5 place-items-center rounded-full align-baseline transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${
                  added ? "bg-accent/15 text-accent" : "text-ink/45 hover:bg-ink/[0.06] hover:text-ink"
                }`}
              >
                <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5">
                  <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.3" />
                  <circle cx="8" cy="4.8" r="0.9" fill="currentColor" />
                  <line x1="8" y1="7.2" x2="8" y2="11.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          );
        }
        // A real link (or a person link with no handler) — open externally.
        if (person) return <>{children}</>;
        // App-internal links (tour follow-ups, deep links) navigate in place, no new tab.
        // Rendered as accent chips — same look as the tour's section-question chips.
        if (href?.startsWith("/")) {
          return (
            <a
              href={href}
              onClick={(e) => {
                e.preventDefault();
                navigate(href);
              }}
              className="box-flat my-0.5 inline-block rounded-lg border-2 border-accent px-3 py-1 text-[12px] font-semibold text-accent no-underline transition hover:bg-ink/[0.04]"
            >
              {children}
            </a>
          );
        }
        return (
          <a href={href} target="_blank" rel="noreferrer" className="text-accent underline underline-offset-2">
            {children}
          </a>
        );
      },
    }),
    [onPersonInfo, infoLabel, addedIds, expandLabel, navigate],
  );

  const display = streaming ? content.replace(TRAILING_OPEN_LINK, "$1") : content;

  return (
    <>
      {/* break-words (overflow-wrap, inherited): a long unbreakable token — e.g. a citation source
          filename or URL with no spaces/hyphens to break on — would otherwise overflow the bubble. */}
      <div className="break-words">
        <Markdown remarkPlugins={[remarkGfm]} components={components} urlTransform={urlTransform}>
          {display}
        </Markdown>
      </div>
      {zoom && (
        <Lightbox src={zoom.src} alt={zoom.alt} closeLabel={closeLabel} onClose={() => setZoom(null)} />
      )}
    </>
  );
});
